import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { FolderUp, Files, CheckCircle2, ArrowRight } from "lucide-react";
import { BulkLegacyRow } from "@/components/BulkLegacyRow";
import { fmtMoney } from "@/lib/format";

const BATCH = 5;

const toRow = (it, i) => ({
    key: `${i}-${it.filename}`, filename: it.filename, staging_id: it.staging_id,
    error: it.error, has_text: it.has_text, matched_on: it.matched_on,
    duplicate_of: it.duplicate_of, customer: it.customer,
    amount: it.fields?.amount ?? "", amount_source: it.fields?.amount_source,
    invoice_date: it.fields?.invoice_date || "", due_date: it.fields?.due_date || "",
    legacy_invoice_number: it.fields?.legacy_invoice_number || "",
    status: "open", action: it.error ? "skip" : it.suggested_action,
});

/** Drop a folder of old-system invoice PDFs → auto-match → review → create. */
export function BulkLegacyInvoiceDialog({ open, setOpen, onDone }) {
    const [step, setStep] = useState("upload");
    const [rows, setRows] = useState([]);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const reset = () => { setStep("upload"); setRows([]); setProgress(null); setResult(null); };

    const analyze = async (fileList) => {
        const pdfs = Array.from(fileList || []).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
        if (!pdfs.length) return toast.error("No PDF files found");
        setBusy(true);
        setProgress({ done: 0, total: pdfs.length });
        const all = [];
        try {
            for (let i = 0; i < pdfs.length; i += BATCH) {
                const fd = new FormData();
                pdfs.slice(i, i + BATCH).forEach((f) => fd.append("files", f));
                const { data } = await api.post("/legacy-invoices/bulk/preview", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                all.push(...data.items);
                setProgress({ done: Math.min(i + BATCH, pdfs.length), total: pdfs.length });
            }
            setRows(all.map(toRow));
            setStep("review");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Analysis failed");
        } finally { setBusy(false); }
    };

    const commit = async () => {
        setBusy(true);
        try {
            const items = rows.filter((r) => r.staging_id).map((r) => ({
                staging_id: r.staging_id, filename: r.filename, action: r.action,
                customer_id: r.customer?.id, amount: parseFloat(r.amount) || 0,
                invoice_date: r.invoice_date, due_date: r.due_date, status: r.status,
                legacy_invoice_number: r.legacy_invoice_number,
                allow_duplicate: !!r.duplicate_of && r.action === "create",
            }));
            const { data } = await api.post("/legacy-invoices/bulk/commit", { items });
            setResult(data);
            setStep("done");
            toast.success(`Created ${data.created} legacy invoice${data.created === 1 ? "" : "s"}`);
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Commit failed");
        } finally { setBusy(false); }
    };

    const update = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

    return (
        <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <SheetContent className="w-full sm:max-w-6xl overflow-y-auto" data-testid="bulk-legacy-dialog">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2"><FolderUp className="w-4 h-4" />Bulk legacy invoices</SheetTitle>
                    <SheetDescription>
                        Drop your Docket invoice PDFs. Each one is read, matched to a customer (account # → name + address → name → filename),
                        and the next auto-invoice is scheduled 90 days after its invoice date.
                    </SheetDescription>
                </SheetHeader>
                {step === "upload" && <UploadStep busy={busy} progress={progress} onFiles={analyze} />}
                {step === "review" && (
                    <ReviewStep rows={rows} update={update} busy={busy} onBack={reset} onCommit={commit} />
                )}
                {step === "done" && result && (
                    <DoneStep result={result} onClose={() => { reset(); setOpen(false); }} />
                )}
            </SheetContent>
        </Sheet>
    );
}

function UploadStep({ busy, progress, onFiles }) {
    const filesRef = useRef(null);
    const folderRef = useRef(null);
    const [over, setOver] = useState(false);
    return (
        <div className="mt-6 space-y-4">
            <div
                onDragOver={(e) => { e.preventDefault(); setOver(true); }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) onFiles(e.dataTransfer.files); }}
                className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors ${over ? "border-primary bg-primary/5" : "border-border"}`}
                data-testid="bulk-legacy-dropzone">
                <FolderUp className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <div className="text-sm font-medium mb-1">Drop invoice PDFs here</div>
                <div className="text-xs text-muted-foreground mb-4">Select all the PDFs inside a folder and drag them in, or pick below</div>
                <div className="flex justify-center gap-2">
                    <Button variant="outline" size="sm" disabled={busy} onClick={() => filesRef.current?.click()}
                            data-testid="bulk-legacy-pick-files"><Files className="w-4 h-4 mr-1" />Choose PDFs</Button>
                    <Button variant="outline" size="sm" disabled={busy} onClick={() => folderRef.current?.click()}
                            data-testid="bulk-legacy-pick-folder"><FolderUp className="w-4 h-4 mr-1" />Choose folder</Button>
                </div>
                <input ref={filesRef} type="file" multiple accept="application/pdf,.pdf" className="hidden"
                       onChange={(e) => onFiles(e.target.files)} data-testid="bulk-legacy-file-input" />
                <input ref={folderRef} type="file" multiple webkitdirectory="" directory="" className="hidden"
                       onChange={(e) => onFiles(e.target.files)} data-testid="bulk-legacy-folder-input" />
            </div>
            {progress && busy && (
                <div className="space-y-1" data-testid="bulk-legacy-progress">
                    <Progress value={(progress.done / progress.total) * 100} />
                    <div className="text-xs text-muted-foreground">Reading {progress.done} / {progress.total} PDFs…</div>
                </div>
            )}
        </div>
    );
}

function ReviewStep({ rows, update, busy, onBack, onCommit }) {
    const creating = rows.filter((r) => r.action === "create");
    const unmatched = rows.filter((r) => !r.customer && !r.error).length;
    const dups = rows.filter((r) => r.duplicate_of).length;
    const invalid = creating.some((r) => !r.customer || !(parseFloat(r.amount) > 0) || !r.invoice_date);
    const total = creating.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    return (
        <div className="mt-6 space-y-4">
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground" data-testid="bulk-legacy-summary">
                <span><b className="text-foreground">{rows.length}</b> PDFs</span>
                <span><b className="text-foreground">{rows.length - unmatched}</b> matched</span>
                <span className={unmatched ? "text-red-600" : ""}><b>{unmatched}</b> need a customer</span>
                <span className={dups ? "text-amber-600" : ""}><b>{dups}</b> duplicates</span>
            </div>
            <div className="border border-border rounded-md overflow-x-auto" data-testid="bulk-legacy-table">
                <table className="w-full text-xs">
                    <thead className="bg-secondary/40 text-muted-foreground">
                        <tr>{["File", "Customer", "Amount", "Invoice date", "Due date", "Old #", "Status", "Action"].map((h) => (
                            <th key={h} className="text-left font-medium px-2 py-2 whitespace-nowrap">{h}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => <BulkLegacyRow key={r.key} index={i} row={r} onChange={(p) => update(r.key, p)} />)}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground">
                    {invalid ? <span className="text-red-600">Rows set to Create need a customer, amount and invoice date</span>
                        : <>On commit: <b>{creating.length}</b> invoices · {fmtMoney(total)}</>}
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={onBack}>Back</Button>
                    <Button onClick={onCommit} disabled={busy || invalid || !creating.length} data-testid="bulk-legacy-commit">
                        {busy ? "Creating..." : <>Create {creating.length} invoice{creating.length === 1 ? "" : "s"}<ArrowRight className="w-4 h-4 ml-1" /></>}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function DoneStep({ result, onClose }) {
    return (
        <div className="mt-6 space-y-4" data-testid="bulk-legacy-done">
            <div className="flex items-center gap-3 p-4 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
                <div className="text-sm" data-testid="bulk-legacy-result">
                    <b>{result.created}</b> created · {result.skipped} skipped{result.errors > 0 && ` · ${result.errors} errors`}
                </div>
            </div>
            {result.error_details?.length > 0 && (
                <div className="border rounded p-2 space-y-1 max-h-40 overflow-y-auto" data-testid="bulk-legacy-errors">
                    {result.error_details.map((e, i) => (
                        <div key={i} className="text-xs"><b>{e.filename}</b> <span className="text-muted-foreground">— {e.reason}</span></div>
                    ))}
                </div>
            )}
            <div className="flex justify-end"><Button onClick={onClose} data-testid="bulk-legacy-close">Done</Button></div>
        </div>
    );
}
